/**
 * Turns the on-screen slides into files in the browser: PNG per slide, a ZIP of them, and a one-page PDF.
 * The libraries are loaded only when someone actually downloads, so the rest of the app stays light.
 */

const dataUrlToBytes = async (dataUrl) => new Uint8Array(await (await fetch(dataUrl)).arrayBuffer());

const toPng = async (node, options) => {
  const { toPng: render } = await import('html-to-image');
  if (document.fonts?.ready) await document.fonts.ready;
  await render(node, options).catch(() => null);   // warm-up: Safari can miss fonts and images on the first pass
  return render(node, options);
};

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/** @returns {{slides: {name:string, blob:Blob}[], zip:Blob, pdf:Blob}} */
export const buildFiles = async ({ slideNodes, cvNode, labels }) => {
  const [{ zipSync }, { jsPDF }] = await Promise.all([import('fflate'), import('jspdf')]);

  const slides = [];
  const zipEntries = {};
  for (let i = 0; i < slideNodes.length; i += 1) {
    const dataUrl = await toPng(slideNodes[i], { width: 1080, height: 1350, pixelRatio: 1 });
    const bytes = await dataUrlToBytes(dataUrl);
    const name = `wifey-application-${String(i + 1).padStart(2, '0')}-${slug(labels[i])}.png`;
    slides.push({ name, blob: new Blob([bytes], { type: 'image/png' }) });
    zipEntries[name] = bytes;
  }
  const zip = new Blob([zipSync(zipEntries, { level: 0 })], { type: 'application/zip' });

  const cvUrl = await toPng(cvNode, { width: 816, height: 1056, pixelRatio: 2 });
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
  pdf.addImage(cvUrl, 'PNG', 0, 0, 612, 792);
  return { slides, zip, pdf: pdf.output('blob') };
};

export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
